import React, { useState } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Popconfirm,
  Tag,
  message,
} from "antd";
import {
  DeleteOutlined,
  PlusOutlined,
  EnvironmentOutlined,
  MenuOutlined,
} from "@ant-design/icons";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "@hello-pangea/dnd";
import { Role } from "../types";

interface RoleManagerProps {
  roles: Role[];
  onAddRole: (role: Omit<Role, "id">) => void;
  onDeleteRole: (id: string) => void;
  onReorderRoles: (roles: Role[]) => void;
}

// --- UPDATED: Draggable Row Component ---
const DraggableBodyRow = ({
  children,
  style,
  className,
  ...restProps
}: any) => {
  const { "data-row-key": index } = restProps;

  // If no index (header or other), render normal row
  if (index === undefined) {
    return (
      <tr {...restProps} className={className} style={style}>
        {children}
      </tr>
    );
  }

  return (
    <Draggable
      draggableId={restProps["data-row-key"].toString()}
      index={restProps.index}
    >
      {(provided, snapshot) => {
        return (
          <tr
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps} // <--- FIXED: Added this line
            {...restProps}
            className={`${className} ${
              snapshot.isDragging ? "is-dragging" : ""
            }`}
            style={{
              ...style,
              ...provided.draggableProps.style,
              background: snapshot.isDragging ? "#e6f7ff" : "inherit",
              display: snapshot.isDragging ? "table" : "table-row",
            }}
          >
            {children}
          </tr>
        );
      }}
    </Draggable>
  );
};

export const RoleManager: React.FC<RoleManagerProps> = ({
  roles,
  onAddRole,
  onDeleteRole,
  onReorderRoles,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form] = Form.useForm();

  const handleSubmit = (values: any) => {
    onAddRole(values);
    message.success("Role created successfully");
    setIsModalOpen(false);
    form.resetFields();
  };

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;

    if (sourceIndex === destinationIndex) return;

    const newRoles = Array.from(roles);
    const [removed] = newRoles.splice(sourceIndex, 1);
    newRoles.splice(destinationIndex, 0, removed);

    onReorderRoles(newRoles);
  };

  const columns = [
    {
      title: "",
      dataIndex: "sort",
      width: 30,
      className: "drag-visible",
      render: () => <MenuOutlined style={{ cursor: "grab", color: "#999" }} />,
    },
    {
      title: "Role Title",
      dataIndex: "title",
      key: "title",
      render: (text: string) => <span style={{ fontWeight: 600 }}>{text}</span>,
    },
    {
      title: "Department",
      dataIndex: "department",
      key: "department",
      render: (dept: string) => {
        let color = "blue";
        if (dept === "Engineering") color = "purple";
        if (dept === "Product") color = "gold";
        if (dept === "Design") color = "magenta";
        return <Tag color={color}>{dept}</Tag>;
      },
    },
    {
      title: "Location",
      dataIndex: "location",
      key: "location",
      render: (loc: string) => (
        <span>
          <EnvironmentOutlined style={{ marginRight: 4 }} />
          {loc || "Remote"}
        </span>
      ),
    },
    {
      title: "Action",
      key: "action",
      render: (_: any, record: Role) => (
        <Popconfirm
          title="Delete this role?"
          description="This will not delete candidates assigned to this role."
          onConfirm={() => onDeleteRole(record.id)}
        >
          <Button type="text" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    },
  ];

  return (
    <div style={{ background: "#fff", padding: 24, borderRadius: 8 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: 16,
        }}
      >
        <h2>Open Positions</h2>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setIsModalOpen(true)}
        >
          Add Role
        </Button>
      </div>

      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="role-table-droppable">
          {(provided) => (
            <div ref={provided.innerRef} {...provided.droppableProps}>
              <Table
                dataSource={roles}
                columns={columns}
                rowKey="id"
                pagination={false}
                components={{
                  body: {
                    row: DraggableBodyRow,
                  },
                }}
                onRow={(record, index) =>
                  ({
                    index,
                    "data-row-key": record.id,
                  } as any)
                }
              />
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      <Modal
        title="Create New Role"
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={null}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="title"
            label="Job Title"
            rules={[{ required: true }]}
          >
            <Input placeholder="e.g. Senior Marketing Manager" />
          </Form.Item>

          <Form.Item
            name="department"
            label="Department"
            rules={[{ required: true }]}
          >
            <Select>
              <Select.Option value="C-Level">C-Level</Select.Option>
              <Select.Option value="Operations">Operations</Select.Option>
              <Select.Option value="Accounting">Accounting</Select.Option>
              <Select.Option value="Engineering">Engineering</Select.Option>
              <Select.Option value="Product">Product</Select.Option>
              <Select.Option value="Design">Design</Select.Option>
              <Select.Option value="Marketing">Marketing</Select.Option>
              <Select.Option value="HR">HR</Select.Option>
              <Select.Option value="Sales">Sales</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item name="location" label="Location" initialValue="Remote">
            <Input placeholder="e.g. Remote, New York, London" />
          </Form.Item>

          <Form.Item style={{ textAlign: "right", marginTop: 24 }}>
            <Button
              onClick={() => setIsModalOpen(false)}
              style={{ marginRight: 8 }}
            >
              Cancel
            </Button>
            <Button type="primary" htmlType="submit">
              Create Role
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
